/**
 * useSignupEmailConfirmation — handles signup confirmation links landing on
 * any page with ?token_hash=<hash>&type=signup.
 *
 * Background: send-email builds signup links as `${siteUrl}?token_hash=…&type=signup`
 * (cross-device safe — no PKCE code_verifier needed). Until now no page handled
 * `type=signup`, so the token was dropped, Supabase never saw a verify call,
 * and accounts stayed unconfirmed (login failed with email_not_confirmed).
 * Recovery already works via ResetPasswordPage; this hook is the signup twin.
 *
 * On success: strips the params, shows a success toast, and navigates to the
 * login page. The user signs in with their password (no auto sign-in).
 * On failure (expired/used token): strips the params and shows an error toast.
 *
 * Must be mounted inside BrowserRouter (uses useNavigate).
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { useLocalizations } from '@/hooks/useLocalizations';
import { ROUTES } from '@/constants';

/**
 * Captured once at module load (before React/router/redirect code runs) so no
 * early navigation on "/" can drop the token before verifyOtp is called.
 */
const INITIAL_SIGNUP_TOKEN_HASH: string | null = ((): string | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('type') === 'signup' ? params.get('token_hash') : null;
})();

let consumedTokenHash: string | null = null;

export function useSignupEmailConfirmation(): void {
  const navigate = useNavigate();
  const { lCommon } = useLocalizations();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;

    const url = new URL(window.location.href);
    const liveTokenHash =
      url.searchParams.get('type') === 'signup' ? url.searchParams.get('token_hash') : null;
    const tokenHash = liveTokenHash || INITIAL_SIGNUP_TOKEN_HASH;

    if (!tokenHash || tokenHash === consumedTokenHash) return;
    ranRef.current = true;
    consumedTokenHash = tokenHash;

    const stripParams = (): void => {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('token_hash');
      clean.searchParams.delete('type');
      window.history.replaceState({}, '', clean.pathname + clean.search + clean.hash);
    };

    const run = async (): Promise<void> => {
      try {
        const { error } = await supabase.auth.verifyOtp({
          type: 'signup',
          token_hash: tokenHash,
        });

        stripParams();

        if (error) {
          logger.warn('[SignupConfirm] verifyOtp failed', { error: error.message });
          toast.error(
            lCommon('toast.email_confirmation_failed.error', {
              defaultValue:
                'This confirmation link is invalid or has expired. Please sign in and request a new verification email.',
            }),
          );
          return;
        }

        toast.success(
          lCommon('toast.email_confirmed.success', {
            defaultValue: 'Email confirmed! You can now sign in.',
          }),
        );
        navigate(ROUTES.AUTH, { replace: true });
      } catch (e) {
        stripParams();
        logger.warn('[SignupConfirm] verifyOtp threw', e);
        toast.error(
          lCommon('toast.email_confirmation_failed.error', {
            defaultValue:
              'This confirmation link is invalid or has expired. Please sign in and request a new verification email.',
          }),
        );
      }
    };

    void run();
  }, [navigate, lCommon]);
}
